/**
 * SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/workflowService.ts
 * PURPOSE: Sprint 11 Workflow Engine API Service
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * APPROVED: 2025-12-02 by CTO (Stage 02 Design Document S11-FE-DESIGN-001)
 * COMPLIANCE: SDLC 6.3.1 + Multi-Industry BPM + TypeScript Best Practices
 * CTO MANDATE: Frontend service for Generic 5-State Workflow Engine
 *
 * @business-context
 * - Domain: Multi-Industry BPM (F&B, Cleaning, Nursing, Healthcare, Hospitality)
 * - Impact: Foundation for ₫38B TAM across 5 industries
 * - Stakeholders: Frontend Team, Remote Team
 *
 * @technical-architecture
 * - Layer: Service Layer
 * - Pattern: API Service, TypeScript interfaces
 * - Dependencies: axios, API types
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  */

import axios from 'axios';

const API_BASE = '/api/v1/workflows';

// ============= Types & Interfaces =============

export type WorkflowState =
  | 'ASSIGNMENT'
  | 'CHECK_IN'
  | 'IN_PROGRESS'
  | 'QUALITY_CHECK'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'ON_HOLD';

export type IndustryCode = 'FB' | 'CL' | 'NR' | 'HC' | 'HT';

export interface WorkflowTemplate {
  id: string;
  industry: IndustryCode;
  workflow_type: string;
  display_name: string;
  display_name_vi: string | null;
  description: string | null;
  bcc_prefix: string;
  state_config: Record<string, StateConfig>;
  state_requirements: Record<string, StateRequirement>;
  is_active: boolean;
}

export interface StateConfig {
  display_name: string;
  transitions: WorkflowState[];
  hooks?: string[];
  required_fields?: string[];
}

export interface StateRequirement {
  requires_gps?: boolean;
  gps_accuracy_threshold?: number;
  requires_photo?: boolean;
  photo_type?: string;
  requires_qc_score?: boolean;
  min_qc_score?: number;
  requires_signature?: boolean;
  signature_type?: string;
}

export interface WorkflowInstance {
  id: string;
  bcc_code: string;
  template_id: string;
  current_state: WorkflowState;
  state_entered_at: string | null;
  tenant_id: string;
  organization_id: string | null;
  branch_id: string | null;
  assigned_to: string | null;
  assigned_by: string | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  metadata: Record<string, unknown>;
  /** Instance-level metadata - industry-specific data */
  instance_metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  is_completed: boolean;
  /** Optional template data - populated when fetching instance with expand=template */
  template?: WorkflowTemplate;
}

export interface CreateWorkflowInstanceRequest {
  template_id: string;
  scheduled_start?: string;
  scheduled_end?: string;
  assigned_to?: string;
  metadata?: Record<string, unknown>;
}

export interface TransitionStateRequest {
  to_state: WorkflowState;
  reason?: string;
  notes?: string;
  gps_latitude?: number;
  gps_longitude?: number;
  gps_accuracy_meters?: number;
  photo_url?: string;
  qc_score?: number;
  qc_notes?: string;
}

export interface TransitionResponse {
  success: boolean;
  instance_id: string;
  bcc_code: string;
  from_state: WorkflowState;
  to_state: WorkflowState;
  transition_id: string;
  transitioned_at: string;
  message: string;
  message_vi: string;
}

export interface StateTransition {
  id: string;
  instance_id: string;
  from_state: WorkflowState;
  to_state: WorkflowState;
  transitioned_by: string | null;
  transitioned_at: string;
  reason: string | null;
  notes: string | null;
  gps: {
    latitude: number;
    longitude: number;
    accuracy_meters: number;
  } | null;
  photo_url: string | null;
  qc: {
    score: number;
    inspector_id: string | null;
    notes: string | null;
  } | null;
  duration_seconds: number | null;
}

export interface WorkflowHistory {
  instance_id: string;
  bcc_code: string;
  current_state: WorkflowState;
  transition_count: number;
  transitions: StateTransition[];
}

export interface WorkflowStats {
  total_instances: number;
  by_state: Record<WorkflowState, number>;
  by_industry: Record<IndustryCode, number>;
  completed_today: number;
  in_progress: number;
}

export interface ListInstancesParams {
  state?: WorkflowState;
  template_id?: string;
  assigned_to?: string;
  from_date?: string;
  to_date?: string;
  limit?: number;
  offset?: number;
}

// ============= Vietnamese Labels =============

export const WORKFLOW_STATE_LABELS: Record<WorkflowState, { en: string; vi: string }> = {
  ASSIGNMENT: { en: 'Assignment', vi: 'Phân công' },
  CHECK_IN: { en: 'Check In', vi: 'Chấm công vào' },
  IN_PROGRESS: { en: 'In Progress', vi: 'Đang thực hiện' },
  QUALITY_CHECK: { en: 'Quality Check', vi: 'Kiểm tra chất lượng' },
  COMPLETED: { en: 'Completed', vi: 'Hoàn thành' },
  CANCELLED: { en: 'Cancelled', vi: 'Đã hủy' },
  ON_HOLD: { en: 'On Hold', vi: 'Tạm dừng' },
};

export const INDUSTRY_LABELS: Record<IndustryCode, { en: string; vi: string }> = {
  FB: { en: 'F&B', vi: 'Nhà hàng/Ẩm thực' },
  CL: { en: 'Cleaning', vi: 'Vệ sinh công nghiệp' },
  NR: { en: 'Nursing', vi: 'Điều dưỡng' },
  HC: { en: 'Healthcare', vi: 'Y tế' },
  HT: { en: 'Hospitality', vi: 'Khách sạn' },
};

// ============= State Color Mapping =============

export const WORKFLOW_STATE_COLORS: Record<WorkflowState, string> = {
  ASSIGNMENT: '#3B82F6', // blue-500
  CHECK_IN: '#8B5CF6', // violet-500
  IN_PROGRESS: '#F59E0B', // amber-500
  QUALITY_CHECK: '#06B6D4', // cyan-500
  COMPLETED: '#10B981', // emerald-500
  CANCELLED: '#EF4444', // red-500
  ON_HOLD: '#6B7280', // gray-500
};

// ============= API Service =============

export const workflowService = {
  // Templates
  async getTemplates(industry?: IndustryCode, isActive?: boolean): Promise<WorkflowTemplate[]> {
    const params = new URLSearchParams();
    if (industry) params.append('industry', industry);
    if (isActive !== undefined) params.append('is_active', String(isActive));

    const response = await axios.get<WorkflowTemplate[]>(`${API_BASE}/templates?${params}`);
    return response.data;
  },

  async getTemplateById(templateId: string): Promise<WorkflowTemplate> {
    const response = await axios.get<WorkflowTemplate>(`${API_BASE}/templates/${templateId}`);
    return response.data;
  },

  async getTemplatesByIndustry(industry: IndustryCode): Promise<WorkflowTemplate[]> {
    const response = await axios.get<WorkflowTemplate[]>(`${API_BASE}/templates/industry/${industry}`);
    return response.data;
  },

  async getActiveTemplates(): Promise<WorkflowTemplate[]> {
    const response = await axios.get<WorkflowTemplate[]>(`${API_BASE}/templates/active`);
    return response.data;
  },

  // Instances
  async getInstances(params?: ListInstancesParams): Promise<WorkflowInstance[]> {
    const queryParams = new URLSearchParams();
    if (params?.state) queryParams.append('state', params.state);
    if (params?.template_id) queryParams.append('template_id', params.template_id);
    if (params?.assigned_to) queryParams.append('assigned_to', params.assigned_to);
    if (params?.from_date) queryParams.append('from_date', params.from_date);
    if (params?.to_date) queryParams.append('to_date', params.to_date);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));

    const response = await axios.get<WorkflowInstance[]>(`${API_BASE}/instances?${queryParams}`);
    return response.data;
  },

  async createInstance(request: CreateWorkflowInstanceRequest): Promise<WorkflowInstance> {
    const response = await axios.post<WorkflowInstance>(`${API_BASE}/instances`, request);
    return response.data;
  },

  async getInstanceById(instanceId: string): Promise<WorkflowInstance> {
    const response = await axios.get<WorkflowInstance>(`${API_BASE}/instances/${instanceId}`);
    return response.data;
  },

  async getInstanceByBccCode(bccCode: string): Promise<WorkflowInstance> {
    const response = await axios.get<WorkflowInstance>(`${API_BASE}/instances/bcc/${bccCode}`);
    return response.data;
  },

  async transitionState(
    instanceId: string,
    request: TransitionStateRequest
  ): Promise<TransitionResponse> {
    const response = await axios.post<TransitionResponse>(
      `${API_BASE}/instances/${instanceId}/transition`,
      request
    );
    return response.data;
  },

  async getInstanceHistory(instanceId: string): Promise<WorkflowHistory> {
    const response = await axios.get<WorkflowHistory>(`${API_BASE}/instances/${instanceId}/history`);
    return response.data;
  },

  // Statistics
  async getStats(industry?: IndustryCode): Promise<WorkflowStats> {
    const params = industry ? `?industry=${industry}` : '';
    const response = await axios.get<WorkflowStats>(`${API_BASE}/stats${params}`);
    return response.data;
  },
};

// ============= Utility Functions =============

export function getStateLabel(state: WorkflowState, lang: 'en' | 'vi' = 'vi'): string {
  return WORKFLOW_STATE_LABELS[state]?.[lang] || state;
}

export function getIndustryLabel(industry: IndustryCode, lang: 'en' | 'vi' = 'vi'): string {
  return INDUSTRY_LABELS[industry]?.[lang] || industry;
}

export function getStateColor(state: WorkflowState): string {
  return WORKFLOW_STATE_COLORS[state] || '#6B7280';
}

export function isTerminalState(state: WorkflowState): boolean {
  return state === 'COMPLETED' || state === 'CANCELLED';
}

export function canTransitionTo(fromState: WorkflowState, toState: WorkflowState): boolean {
  const validTransitions: Record<WorkflowState, WorkflowState[]> = {
    ASSIGNMENT: ['CHECK_IN', 'CANCELLED'],
    CHECK_IN: ['IN_PROGRESS', 'CANCELLED', 'ON_HOLD'],
    IN_PROGRESS: ['QUALITY_CHECK', 'CANCELLED', 'ON_HOLD'],
    QUALITY_CHECK: ['COMPLETED', 'IN_PROGRESS', 'CANCELLED'],
    COMPLETED: [],
    CANCELLED: [],
    ON_HOLD: ['IN_PROGRESS', 'CANCELLED'],
  };

  return validTransitions[fromState]?.includes(toState) || false;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

export default workflowService;
